import React from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, Gift, ShoppingCart, SlidersHorizontal, CalendarDays, X } from "lucide-react";
import { PricingCard } from "@/components/PricingCard";
import { DemoManagementCard } from "@/components/DemoManagementCard";
import { PersonalDemoInvitesCard } from "@/components/PersonalDemoInvitesCard";

const EVENT_LABELS = {
  payment_succeeded: { label: "Zahlung erhalten", cls: "bg-green-100 text-green-700" },
  refund: { label: "Rückerstattung", cls: "bg-amber-100 text-amber-700" },
  payment_failed: { label: "Zahlung fehlgeschlagen", cls: "bg-red-100 text-red-700" },
  account_updated: { label: "Partner-Onboarding aktualisiert", cls: "bg-blue-100 text-blue-700" },
};

const PARTNER_STUDIOS = [
  { id: "crm", label: "CRM Studio" },
  { id: "travel", label: "Business Travel Studio" },
  { id: "workforce", label: "Project & Workforce Studio" },
  { id: "expense_studio", label: "Expense & Document Studio" },
  { id: "commerce", label: "Commerce Studio" },
];

const TIER_OPTIONS = [
  { value: "basic", label: "Starter" },
  { value: "business", label: "Pro" },
  { value: "premium", label: "Business" },
];

const formatDate = (value) => value ? new Date(`${value}T12:00:00`).toLocaleDateString("de-DE") : "Unbefristet";
const statusLabel = (status) => status === "expired" ? "Abgelaufen" : status === "revoked" ? "Entzogen" : "Aktiv";
const statusClass = (status) => status === "active" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";

export const PlatformTab = ({
  platform, ablefy, ablefyWebhookUrl, grantEmail, setGrantEmail, grantPlan, setGrantPlan,
  grantBusy, grantAccess, compAccounts, revokeAccess, fetchPlatform, fetchAblefy,
  mapProductId, setMapProductId, mapName, setMapName, mapPlan, setMapPlan, mapBusy,
  mapCheckoutUrl, setMapCheckoutUrl, mapBillingInterval, setMapBillingInterval,
  workshopBundleUrl, setWorkshopBundleUrl, workshopBundleInterval, setWorkshopBundleInterval,
  saveWorkshopBundleLink, saveAblefyMap, deleteAblefyMap, updateCompRights,
}) => {
  const [editingAccount, setEditingAccount] = React.useState(null);
  const [rightsSaving, setRightsSaving] = React.useState(false);

  const openRights = (account) => {
    setEditingAccount({
      email: account.email,
      company: account.company,
      plan: account.plan || "business",
      modules: { ...(account.modules || {}) },
      access_expires_on: account.access_expires_on || "",
    });
  };

  const toggleStudio = (studioId, selected) => {
    setEditingAccount((current) => ({
      ...current,
      modules: selected
        ? { ...current.modules, [studioId]: current.modules[studioId] || "premium" }
        : Object.fromEntries(Object.entries(current.modules).filter(([id]) => id !== studioId)),
    }));
  };

  const saveRights = async () => {
    if (!editingAccount) return;
    if (Object.keys(editingAccount.modules).length === 0) {
      toast.error("Bitte mindestens ein Studio ausgewählt lassen.");
      return;
    }
    setRightsSaving(true);
    try {
      await updateCompRights(editingAccount);
      toast.success("Partnerrechte gespeichert. Es wurde keine E-Mail versendet.");
      setEditingAccount(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Partnerrechte konnten nicht gespeichert werden");
    } finally {
      setRightsSaving(false);
    }
  };

  return (
            <div data-testid="platform-view" className="space-y-6 max-w-5xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)]">Plattform-Übersicht (White-Label-Partner)</h2>
                  <p className="text-sm text-[var(--brand-muted)]">Deine {platform?.fee_percent ?? 30} % Umsatzbeteiligung aus Weiterverkauf & Vermietung – automatisch über Stripe Connect.</p>
                </div>
                <Button variant="outline" size="sm" onClick={fetchPlatform} className="border-[var(--brand-border)]" data-testid="platform-refresh">
                  <RefreshCw className="w-4 h-4 mr-1" /> Aktualisieren
                </Button>
              </div>

              {/* Gratis-Zugang verwalten */}
              <Card className="p-5 border-[var(--brand-border)]" data-testid="grant-access-card">
                <div className="flex items-start gap-2 mb-1">
                  <Gift className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-[var(--brand-ink)]">Zugang freischalten (gratis)</div>
                    <p className="text-xs text-[var(--brand-muted)]">Schalte ein Konto kostenlos frei – z.&nbsp;B. für Vertriebspartnerinnen. Die Person muss sich vorher einmal mit ihrer E-Mail registriert haben.</p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <Input
                    type="email"
                    placeholder="E-Mail des Kontos"
                    value={grantEmail}
                    onChange={(e) => setGrantEmail(e.target.value)}
                    className="flex-1 border-[var(--brand-border)]"
                    data-testid="grant-email-input"
                  />
                  <select
                    value={grantPlan}
                    onChange={(e) => setGrantPlan(e.target.value)}
                    className="h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm"
                    data-testid="grant-plan-select"
                  >
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="business">Business</option>
                    <option value="business_all">Business + alle Module (Premium)</option>
                  </select>
                  <Button onClick={grantAccess} disabled={grantBusy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="grant-access-btn">
                    {grantBusy ? "…" : "Freischalten"}
                  </Button>
                </div>
                {compAccounts.length > 0 && (
                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="comp-accounts-table">
                      <thead>
                        <tr className="text-left text-[var(--brand-muted)] border-b border-[var(--brand-border)]">
                          <th className="py-2 pr-4">Konto</th>
                          <th className="py-2 pr-4">Tarif</th>
                          <th className="py-2 pr-4">Freigeschaltete Studios</th>
                          <th className="py-2 pr-4">Zugang bis</th>
                          <th className="py-2 pr-4">Status</th>
                          <th className="py-2 text-right">Aktion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compAccounts.map((a) => (
                          <tr key={a.org_id} className="border-b border-[var(--brand-border-light)]">
                            <td className="py-2 pr-4">
                              <div className="font-medium text-[var(--brand-ink)]">{a.company || "—"}</div>
                              <div className="text-xs text-[var(--brand-muted)]">{a.email}</div>
                            </td>
                            <td className="py-2 pr-4 capitalize">{a.plan}</td>
                            <td className="py-2 pr-4">
                              {Object.keys(a.modules || {}).length ? (
                                <div className="flex flex-wrap gap-1">
                                  {Object.keys(a.modules).map((moduleId) => (
                                    <span key={moduleId} className="rounded-full bg-[var(--brand-border-light)] px-2 py-0.5 text-[11px] text-[var(--brand-ink)]">
                                      {PARTNER_STUDIOS.find((studio) => studio.id === moduleId)?.label || moduleId}
                                    </span>
                                  ))}
                                </div>
                              ) : <span className="text-xs text-[var(--brand-muted)]">Keine Zusatzstudios</span>}
                            </td>
                            <td className="py-2 pr-4 text-xs text-[var(--brand-muted)]">{formatDate(a.access_expires_on)}</td>
                            <td className="py-2 pr-4">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${statusClass(a.status)}`}>
                                {statusLabel(a.status)}
                              </span>
                            </td>
                            <td className="py-2 text-right">
                              <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => openRights(a)} className="border-[var(--brand-border)]" data-testid={`manage-${a.email}`}>
                                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1" /> Rechte
                                </Button>
                                {a.status === "active" && (
                                  <Button variant="outline" size="sm" onClick={() => revokeAccess(a.email)} className="border-[var(--brand-border)] text-red-600 hover:bg-red-50" data-testid={`revoke-${a.email}`}>
                                    Entziehen
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Ablefy – Lizenzverkäufe (Merchant of Record) */}
              <Card className="p-5 border-[var(--brand-border)]" data-testid="ablefy-card">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="flex items-start gap-2">
                    <ShoppingCart className="w-5 h-5 text-[var(--brand-primary)] mt-0.5" />
                    <div>
                      <div className="text-sm font-semibold text-[var(--brand-ink)]">Ablefy-Lizenzverkäufe</div>
                      <p className="text-xs text-[var(--brand-muted)]">Käufe über Ablefy werden automatisch getrackt und Konten freigeschaltet. Registriert sich der Käufer später mit derselben E-Mail, greift der Zugang automatisch.</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchAblefy} className="border-[var(--brand-border)]" data-testid="ablefy-refresh">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>

                {ablefy && (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                      <div className="rounded-lg bg-[var(--brand-border-light)] p-3" data-testid="ablefy-stat-activated">
                        <div className="text-xs text-[var(--brand-muted)]">Freigeschaltet</div>
                        <div className="text-2xl font-bold text-[var(--brand-ink)]">{ablefy.stats.activated}</div>
                      </div>
                      <div className="rounded-lg bg-[var(--brand-border-light)] p-3" data-testid="ablefy-stat-pending">
                        <div className="text-xs text-[var(--brand-muted)]">Anwartschaften</div>
                        <div className="text-2xl font-bold text-[var(--brand-ink)]">{ablefy.stats.pending}</div>
                      </div>
                      <div className="rounded-lg bg-[var(--brand-border-light)] p-3" data-testid="ablefy-stat-unmapped">
                        <div className="text-xs text-[var(--brand-muted)]">Ohne Zuordnung</div>
                        <div className="text-2xl font-bold text-amber-600">{ablefy.stats.unmapped}</div>
                      </div>
                      <div className="rounded-lg bg-[var(--brand-border-light)] p-3" data-testid="ablefy-stat-revenue">
                        <div className="text-xs text-[var(--brand-muted)]">Umsatz (Ablefy)</div>
                        <div className="text-2xl font-bold text-[var(--brand-ink)]">{Number(ablefy.stats.revenue || 0).toLocaleString("de-DE")} €</div>
                      </div>
                    </div>

                    {/* Webhook-URL */}
                    <div className="mt-4 rounded-lg border border-[var(--brand-border)] p-3">
                      <div className="text-xs font-semibold text-[var(--brand-ink)] mb-1">Webhook-URL (in Ablefy hinterlegen)</div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs bg-[var(--brand-border-light)] rounded px-2 py-1.5 overflow-x-auto whitespace-nowrap" data-testid="ablefy-webhook-url">{ablefyWebhookUrl}</code>
                        <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(ablefyWebhookUrl); toast.success("Webhook-URL kopiert"); }} className="border-[var(--brand-border)] shrink-0" data-testid="ablefy-copy-url">Kopieren</Button>
                      </div>
                      <p className="text-[11px] text-[var(--brand-muted)] mt-1">In Ablefy unter „Market &amp; Sell → Webhook" als Endpoint eintragen und dem jeweiligen Produkt zuweisen.</p>
                    </div>

	                    {/* Produkt-Zuordnung */}
	                    <div className="mt-4 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg)] p-3" data-testid="workshop-bundle-link">
	                      <div className="text-xs font-semibold text-[var(--brand-ink)]">Werkstatt-Bundle mit Ablefy verbinden</div>
	                      <p className="mt-1 text-[11px] text-[var(--brand-muted)]">Monats- und Jahresartikel werden getrennt hinterlegt. Beide schalten nach dem Kauf ausschließlich Angebots- &amp; Rechnungsstudio Business (99 €) und CRM Studio Business (119 €) frei.</p>
	                      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
	                        <select value={workshopBundleInterval} onChange={(e) => setWorkshopBundleInterval(e.target.value)} className="h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" data-testid="workshop-bundle-interval">
	                          <option value="month">Monatlich · 179 € netto</option>
	                          <option value="year">Jährlich · 1.790 € netto</option>
	                        </select>
	                        <Input type="url" placeholder="Ablefy-Bezahllink des Werkstatt-Bundles" value={workshopBundleUrl} onChange={(e) => setWorkshopBundleUrl(e.target.value)} className="border-[var(--brand-border)] flex-1" data-testid="workshop-bundle-url" />
	                        <Button onClick={saveWorkshopBundleLink} disabled={mapBusy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="workshop-bundle-save">{mapBusy ? "…" : "Bundle verknüpfen"}</Button>
	                      </div>
	                    </div>

	                    <div className="mt-4">
	                      <div className="text-xs font-semibold text-[var(--brand-ink)] mb-2">Produkt → Tarif zuordnen</div>
	                      <div className="flex flex-col sm:flex-row gap-2">
	                        <Input placeholder="Ablefy Produkt-ID" value={mapProductId} onChange={(e) => setMapProductId(e.target.value)} className="border-[var(--brand-border)] sm:w-40" data-testid="ablefy-map-productid" />
	                        <Input placeholder="Produktname (optional)" value={mapName} onChange={(e) => setMapName(e.target.value)} className="border-[var(--brand-border)] flex-1" data-testid="ablefy-map-name" />
	                        <select value={mapPlan} onChange={(e) => setMapPlan(e.target.value)} className="h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" data-testid="ablefy-map-plan">
                          <option value="starter">Starter</option>
                          <option value="pro">Pro</option>
                          <option value="business">Business</option>
                          <option value="whitelabel">White-Label (alles)</option>
                          <option value="crm:basic">CRM Studio Basic</option>
	                          <option value="crm:business">CRM Studio Business</option>
	                          <option value="crm:premium">CRM Studio Premium</option>
	                          <option value="travel:basic">Travel Studio Basic</option>
	                          <option value="travel:business">Travel Studio Business</option>
	                          <option value="travel:premium">Travel Studio Premium</option>
	                          <option value="bundle:werkstatt">Werkstatt-Bundle: Angebot/Rechnung Business + CRM Business</option>
	                        </select>
	                        <Button onClick={saveAblefyMap} disabled={mapBusy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="ablefy-map-save">{mapBusy ? "…" : "Speichern"}</Button>
	                      </div>
	                      {mapPlan === "bundle:werkstatt" && (
	                        <div className="mt-2">
	                          <div className="flex flex-col gap-2 sm:flex-row">
	                            <select value={mapBillingInterval} onChange={(e) => setMapBillingInterval(e.target.value)} className="h-10 rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm" data-testid="ablefy-map-billing-interval">
	                              <option value="month">Monatlich</option>
	                              <option value="year">Jährlich</option>
	                            </select>
	                            <Input type="url" placeholder="Ablefy-Bezahllink des Werkstatt-Bundles" value={mapCheckoutUrl} onChange={(e) => setMapCheckoutUrl(e.target.value)} className="border-[var(--brand-border)] flex-1" data-testid="ablefy-map-checkout-url" />
	                          </div>
	                          <p className="mt-1 text-[11px] text-[var(--brand-muted)]">Der Link wird ausschließlich für das Werkstatt-Bundle gespeichert. Nach einem Kauf werden die zwei bestehenden Studios gemeinsam freigeschaltet.</p>
	                        </div>
	                      )}
	                      <p className="text-[11px] text-[var(--brand-muted)] mt-1">Ohne Zuordnung erkennt das System einzelne Tarife am Produktnamen. Bundles werden immer ausdrücklich über Produkt-ID und Bezahllink zugeordnet.</p>
                      {ablefy.mappings.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {ablefy.mappings.map((m) => (
                            <span key={m.product_id} className="inline-flex items-center gap-2 text-xs bg-[var(--brand-border-light)] rounded-full px-3 py-1" data-testid={`ablefy-map-${m.product_id}`}>
                              <span className="font-mono">{m.product_id}</span> → <span className="capitalize font-medium">{m.plan}</span>{m.plan === "bundle:werkstatt" ? <span>{m.billing_interval === "year" ? " · jährlich" : " · monatlich"}</span> : null}
                              <button onClick={() => deleteAblefyMap(m.product_id)} className="text-red-500 hover:text-red-700" data-testid={`ablefy-map-del-${m.product_id}`}>×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Letzte Käufe */}
                    {ablefy.transactions.length > 0 ? (
                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-sm" data-testid="ablefy-tx-table">
                          <thead>
                            <tr className="text-left text-[var(--brand-muted)] border-b border-[var(--brand-border)]">
                              <th className="py-2 pr-4">Käufer</th>
                              <th className="py-2 pr-4">Produkt</th>
                              <th className="py-2 pr-4">Tarif</th>
                              <th className="py-2 pr-4">Betrag</th>
                              <th className="py-2 pr-4">Status</th>
                              <th className="py-2">Datum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ablefy.transactions.slice(0, 30).map((t) => (
                              <tr key={t.id} className="border-b border-[var(--brand-border-light)]">
                                <td className="py-2 pr-4 text-[var(--brand-ink)]">{t.email || "—"}</td>
                                <td className="py-2 pr-4 text-xs text-[var(--brand-muted)]">{t.product_name || t.product_id || "—"}</td>
                                <td className="py-2 pr-4 capitalize">{t.plan || "—"}</td>
                                <td className="py-2 pr-4">{t.amount ? `${Number(t.amount).toLocaleString("de-DE")} €` : "—"}</td>
                                <td className="py-2 pr-4">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    t.result === "activated" ? "bg-green-100 text-green-700" :
                                    t.result === "pending" ? "bg-blue-100 text-blue-700" :
                                    t.result === "unmapped" ? "bg-amber-100 text-amber-700" :
                                    t.result === "deactivated" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                                    {t.result === "activated" ? "Aktiv" : t.result === "pending" ? "Wartet auf Registrierung" : t.result === "unmapped" ? "Keine Zuordnung" : t.result === "deactivated" ? "Storniert" : t.result}
                                  </span>
                                </td>
                                <td className="py-2 text-xs text-[var(--brand-muted)]">{t.created_at ? new Date(t.created_at).toLocaleString("de-DE") : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-[var(--brand-muted)] mt-4" data-testid="ablefy-empty">Noch keine Ablefy-Käufe eingegangen.</p>
                    )}
                  </>
                )}
              </Card>


              {!platform ? (
                <div className="text-sm text-[var(--brand-muted)]">Lade Daten …</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="text-[var(--brand-muted)] text-xs uppercase tracking-wider mb-1">Partner</div>
                      <div className="text-3xl font-bold text-[var(--brand-ink)]" data-testid="pf-partners">{platform.partner_count}</div>
                      <div className="text-xs text-[var(--brand-muted)]">{platform.active_partner_count} aktiv</div>
                    </Card>
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="text-[var(--brand-muted)] text-xs uppercase tracking-wider mb-1">Partner-Umsatz (brutto)</div>
                      <div className="text-3xl font-bold text-[var(--brand-ink)]" data-testid="pf-gross">{platform.total_gross.toLocaleString("de-DE")} €</div>
                    </Card>
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="text-[var(--brand-muted)] text-xs uppercase tracking-wider mb-1">Dein Anteil ({platform.fee_percent} %)</div>
                      <div className="text-3xl font-bold text-[var(--brand-primary)]" data-testid="pf-share">{platform.total_my_share.toLocaleString("de-DE")} €</div>
                    </Card>
                    <Card className="p-5 border-[var(--brand-border)]">
                      <div className="text-[var(--brand-muted)] text-xs uppercase tracking-wider mb-1">Zahlungen</div>
                      <div className="text-sm mt-1 space-y-0.5">
                        <div className="flex justify-between gap-4"><span className="text-[var(--brand-muted)]">Offen</span><span className="font-semibold">{platform.open_payments}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-[var(--brand-muted)]">Fehlgeschlagen</span><span className="font-semibold">{platform.failed_payments}</span></div>
                        <div className="flex justify-between gap-4"><span className="text-[var(--brand-muted)]">Rückerstattungen</span><span className="font-semibold">{platform.refunds}</span></div>
                      </div>
                    </Card>
                  </div>
                  <Card className="p-5 border-[var(--brand-border)]">
                    <div className="text-sm font-semibold text-[var(--brand-ink)] mb-4">Partner &amp; Umsätze</div>
                    {platform.partners.length === 0 ? (
                      <div className="text-sm text-[var(--brand-muted)]">Noch keine White-Label-Partner mit Stripe Connect verbunden.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="platform-partners-table">
                          <thead>
                            <tr className="text-left text-[var(--brand-muted)] border-b border-[var(--brand-border)]">
                              <th className="py-2 pr-4">Partner</th>
                              <th className="py-2 pr-4">Status</th>
                              <th className="py-2 pr-4">Endkunden</th>
                              <th className="py-2 pr-4 text-right">Umsatz</th>
                              <th className="py-2 pr-4 text-right">Dein Anteil</th>
                              <th className="py-2 text-right">Offen</th>
                            </tr>
                          </thead>
                          <tbody>
                            {platform.partners.map((p) => (
                              <tr key={p.org_id} className="border-b border-[var(--brand-border-light)]">
                                <td className="py-2 pr-4 font-medium text-[var(--brand-ink)]">{p.company}</td>
                                <td className="py-2 pr-4">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${p.charges_enabled ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                                    {p.charges_enabled ? "Aktiv" : "Onboarding offen"}
                                  </span>
                                </td>
                                <td className="py-2 pr-4">{p.customers}</td>
                                <td className="py-2 pr-4 text-right">{p.gross.toLocaleString("de-DE")} €</td>
                                <td className="py-2 pr-4 text-right font-semibold text-[var(--brand-primary)]">{p.my_share.toLocaleString("de-DE")} €</td>
                                <td className="py-2 text-right">{p.open_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                  <Card className="p-5 border-[var(--brand-border)]" data-testid="platform-events-card">
                    <div className="text-sm font-semibold text-[var(--brand-ink)] mb-4">Letzte Ereignisse (Zahlungen, Rückerstattungen, Onboarding)</div>
                    {(!platform.recent_events || platform.recent_events.length === 0) ? (
                      <div className="text-sm text-[var(--brand-muted)]">Noch keine Ereignisse. Sobald Partner-Zahlungen, Rückerstattungen oder Onboarding-Updates über Stripe Connect eintreffen, erscheinen sie hier.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="platform-events-table">
                          <thead>
                            <tr className="text-left text-[var(--brand-muted)] border-b border-[var(--brand-border)]">
                              <th className="py-2 pr-4">Zeitpunkt</th>
                              <th className="py-2 pr-4">Ereignis</th>
                              <th className="py-2 pr-4">Partner</th>
                              <th className="py-2 text-right">Betrag</th>
                            </tr>
                          </thead>
                          <tbody>
                            {platform.recent_events.map((ev, i) => {
                              const meta = EVENT_LABELS[ev.type] || { label: ev.type, cls: "bg-gray-100 text-gray-700" };
                              return (
                                <tr key={ev.id || i} className="border-b border-[var(--brand-border-light)]">
                                  <td className="py-2 pr-4 text-[var(--brand-muted)] whitespace-nowrap">{ev.created_at ? new Date(ev.created_at).toLocaleString("de-DE") : "—"}</td>
                                  <td className="py-2 pr-4"><span className={`text-xs px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span></td>
                                  <td className="py-2 pr-4 text-[var(--brand-ink)]">{ev.company || "—"}</td>
                                  <td className="py-2 text-right">{ev.amount != null ? `${Number(ev.amount).toLocaleString("de-DE")} ${(ev.currency || "eur").toUpperCase()}` : "—"}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                </>
              )}

              {/* Schneller gemeinsamer Demo-Zugang für WhatsApp und Live-Gespräche */}
              <DemoManagementCard />

              {/* Persönliche Einladungen für identifizierbare Verkaufsanfragen */}
              <PersonalDemoInvitesCard />

              {/* Preisverwaltung (DB-basiert, ohne Code-Änderung) */}
              <PricingCard />

              {editingAccount && (
                <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/40 p-3 sm:items-center" role="dialog" aria-modal="true" aria-label="Partnerrechte verwalten">
                  <Card className="max-h-[92vh] w-full max-w-2xl overflow-y-auto border-[var(--brand-border)] bg-white p-5 shadow-xl">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)]">Partnerrechte verwalten</h3>
                        <p className="mt-1 text-sm text-[var(--brand-muted)]">{editingAccount.company || editingAccount.email}</p>
                        <p className="text-xs text-[var(--brand-muted)]">Die Angaben sind nur in deiner Plattformverwaltung sichtbar. Es wird keine E-Mail versendet.</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => setEditingAccount(null)} aria-label="Dialog schließen"><X className="h-4 w-4" /></Button>
                    </div>

                    <div className="space-y-5">
                      <label className="block text-sm font-medium text-[var(--brand-ink)]">
                        Basistarif für Angebots- &amp; Rechnungsstudio
                        <select value={editingAccount.plan} onChange={(event) => setEditingAccount((current) => ({ ...current, plan: event.target.value }))} className="mt-1 h-10 w-full rounded-md border border-[var(--brand-border)] bg-white px-3 text-sm">
                          <option value="starter">Starter</option>
                          <option value="pro">Pro</option>
                          <option value="business">Business</option>
                        </select>
                      </label>

                      <div>
                        <div className="text-sm font-medium text-[var(--brand-ink)]">Zusätzlich freigeschaltete Studios</div>
                        <p className="mt-1 text-xs text-[var(--brand-muted)]">Nicht ausgewählte Studios werden für dieses Partnerkonto serverseitig gesperrt. Lead &amp; Funnel bleibt Teil des CRM Studios.</p>
                        <div className="mt-3 space-y-2">
                          {PARTNER_STUDIOS.map((studio) => {
                            const selected = Boolean(editingAccount.modules[studio.id]);
                            return (
                              <div key={studio.id} className="flex flex-col gap-2 rounded-md border border-[var(--brand-border-light)] p-3 sm:flex-row sm:items-center sm:justify-between">
                                <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--brand-ink)]">
                                  <input type="checkbox" checked={selected} onChange={(event) => toggleStudio(studio.id, event.target.checked)} />
                                  {studio.label}
                                </label>
                                {selected && (
                                  <select value={editingAccount.modules[studio.id]} onChange={(event) => setEditingAccount((current) => ({ ...current, modules: { ...current.modules, [studio.id]: event.target.value } }))} className="h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm">
                                    {TIER_OPTIONS.map((tier) => <option key={tier.value} value={tier.value}>{tier.label}</option>)}
                                  </select>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="rounded-md border border-[var(--brand-border-light)] bg-[var(--brand-bg)] p-3">
                        <div className="flex items-center gap-2 text-sm font-medium text-[var(--brand-ink)]"><CalendarDays className="h-4 w-4" /> Zugang zeitlich begrenzen</div>
                        <p className="mt-1 text-xs text-[var(--brand-muted)]">Das Datum bleibt nur in deiner Plattformverwaltung sichtbar. Nach Ablauf sieht die Person lediglich eine neutrale Kontaktmeldung.</p>
                        <label className="mt-3 flex items-center gap-2 text-sm text-[var(--brand-ink)]"><input type="checkbox" checked={Boolean(editingAccount.access_expires_on)} onChange={(event) => setEditingAccount((current) => ({ ...current, access_expires_on: event.target.checked ? (current.access_expires_on || new Date().toISOString().slice(0, 10)) : "" }))} /> Zugang befristen</label>
                        {editingAccount.access_expires_on ? <Input className="mt-2 max-w-xs border-[var(--brand-border)]" type="date" value={editingAccount.access_expires_on} onChange={(event) => setEditingAccount((current) => ({ ...current, access_expires_on: event.target.value }))} /> : <p className="mt-2 text-xs text-[var(--brand-muted)]">Unbefristet</p>}
                      </div>

                      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                        <Button variant="outline" onClick={() => setEditingAccount(null)} className="border-[var(--brand-border)]">Abbrechen</Button>
                        <Button onClick={saveRights} disabled={rightsSaving} className="bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)]">{rightsSaving ? "Speichert …" : "Rechte speichern"}</Button>
                      </div>
                    </div>
                  </Card>
                </div>
              )}
            </div>

  );
};
